import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ToggleOption from '@/components/common/ToggleOption/ToggleOption';
import {
  ContractBillingStatusPascalCaseEnum,
  ContractStatusEnum,
  ContractLineBillingCycleOutput,
  VariableContractLineOutput
} from '@/graphql/contracts/_generated_/contracts';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import CustomToggleTitleComponent from './components/CustomToggleTitleComponent';
import PauseBillingModal from './components/PauseBilling/PauseBillingModal';
import ResumeBillingModal from './components/ResumeBilling/ResumeBillingModal';
import UpdateBillingModal from './components/UpdateBilling/UpdateBillingModal';
import PauseContractLineModal from './components/PauseContractLine/PauseContractLineModal';
import ResumeContractLineModal from './components/ResumeContractLine/ResumeContractLineModal';
import usePauseBilling from './hooks/usePauseBilling';
import useResumeBilling from './hooks/useResumeBilling';
import useUpdatePauseBilling from './hooks/useUpdatePauseBilling';
import usePauseContractLine from './hooks/usePauseContractLine';
import useResumeContractLine from './hooks/useResumeContractLine';
import { getReasonTypes } from '@/utils/data-helpers/reasonTypes';
import { PAUSE_BILLING_TYPE } from '@/config/reasonTypes.config';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { useSelectedLines, lineRefreshObservable } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';

import { useSelectedVariableLines, variableLineRefreshObservable } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';

const getContractLineId = (contractLine: any): string | undefined => {
  if ('id' in contractLine && contractLine.id) {
    return contractLine.id;
  }
  if ('iD' in contractLine && contractLine.iD) {
    return contractLine.iD;
  }
  return undefined;
};

enum VisibleModal {
  PAUSE,
  UPDATE,
  RESUME,
  PAUSE_CONTRACT_LINE,
  RESUME_CONTRACT_LINE,
  UPDATE_CONTRACT_LINE,
  NONE
}

export default function PauseBillingContainer({ disabled, readOnly }: { disabled?: boolean; readOnly?: boolean }) {
  const { contractId }: { contractId: UUID } = useParams();
  const { contract } = useGetContractDetails(contractId);

  const { pauseBilling } = usePauseBilling(contractId);
  const { updatePauseBilling } = useUpdatePauseBilling(contractId);
  const { resumeBilling } = useResumeBilling(contractId);
  const { pauseContractLine } = usePauseContractLine();
  const { resumeContractLine } = useResumeContractLine();
  const reasonTypes = getReasonTypes();
  const { getReasonCodes, isLoading, reasonCodes } = useGetReasonCodes();

  const selectedLines = useSelectedLines();
  const selectedVariableLines = useSelectedVariableLines();
  const { cachedBCContractLines, cachedVariableContractLines } = useGetContractLinesFromCache(
    contractId
  );

  const [visibleModal, setVisibleModal] = useState<VisibleModal>(VisibleModal.NONE);
  const [isContractLinePauseToggleEnable, setIsContractLinePauseToggleEnable] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const hasRegularLines = selectedLines.length > 0;
  const hasVariableLines = selectedVariableLines.length > 0;
  const isOperatingOnVariableLines = !hasRegularLines && hasVariableLines;
  const activeSelectedLines = isOperatingOnVariableLines ? selectedVariableLines : selectedLines;

  const getContractLineStatus = (contractLine: any): ContractStatusEnum | undefined => {
    return ((contractLine as ContractLineBillingCycleOutput)?.status ||
      (contractLine as VariableContractLineOutput)?.status) as
      | ContractStatusEnum
      | undefined;
  };

  const hasExcludedStatuses = useMemo(() => {
    const excludedStatuses = [
      ContractStatusEnum.Cancelled,
      ContractStatusEnum.Expired,
      ContractStatusEnum.Terminated,
      ContractStatusEnum.Inactive
    ];

    return activeSelectedLines.some(line => {
      const status = getContractLineStatus(line.payload.contractLine);
      return status && excludedStatuses.includes(status);
    });
  }, [activeSelectedLines]);

  const handleContractLineToggleChange = () => {
    setVisibleModal(
      isContractLinePauseToggleEnable ? VisibleModal.RESUME_CONTRACT_LINE : VisibleModal.PAUSE_CONTRACT_LINE
    );
  };

  const handleCloseModal = () => {
    setVisibleModal(VisibleModal.NONE);
  };

  const selectedFreshBCLines = useMemo(() => {
    if (selectedLines.length === 0) return [];

    const selectedLineIds = selectedLines.map(line => {
      const contractLine = line.payload.contractLine;
      return getContractLineId(contractLine);
    }).filter(Boolean);

    if (selectedLineIds.length === 0) return [];

    const freshBCLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];

    const freshSelectedLines = freshBCLines.filter(line => {
      if (!line) return false;
      const lineId = getContractLineId(line);
      return lineId && selectedLineIds.includes(lineId);
    });

    return freshSelectedLines
      .filter((contractLine): contractLine is NonNullable<typeof contractLine> => contractLine !== null)
      .map(contractLine => {
        const lineId = getContractLineId(contractLine);
        return {
          id: lineId || '',
          payload: {
            contractLine: contractLine as ContractLineBillingCycleOutput
          }
        };
      });
  }, [selectedLines, cachedBCContractLines]);

  const selectedFreshVariableLines = useMemo(() => {
    if (selectedVariableLines.length === 0) return [];

    const selectedLineIds = selectedVariableLines.map(line => {
      const contractLine = line.payload.contractLine;
      return getContractLineId(contractLine);
    }).filter(Boolean);

    if (selectedLineIds.length === 0) return [];

    const freshVariableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

    const freshSelectedLines = freshVariableLines.filter(line => {
      if (!line) return false;
      const lineId = getContractLineId(line);
      return lineId && selectedLineIds.includes(lineId);
    });

    return freshSelectedLines
      .filter((contractLine): contractLine is NonNullable<typeof contractLine> => contractLine !== null)
      .map(contractLine => {
        const lineId = getContractLineId(contractLine);
        return {
          id: lineId || '',
          payload: {
            contractLine: contractLine as VariableContractLineOutput
          }
        };
      });
  }, [selectedVariableLines, cachedVariableContractLines]);

  useEffect(() => {
    if (visibleModal === VisibleModal.PAUSE || visibleModal === VisibleModal.UPDATE ||
      visibleModal === VisibleModal.PAUSE_CONTRACT_LINE || visibleModal === VisibleModal.UPDATE_CONTRACT_LINE) {
      getReasonCodes(PAUSE_BILLING_TYPE);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);


  useEffect(() => {
    if (!hasRegularLines && !hasVariableLines) {
      setIsContractLinePauseToggleEnable(false);
      return;
    }

    let allLinesPaused = true;

    if (hasRegularLines) {
      const regularLineIds = selectedLines.map(line => {
        const contractLine = line.payload.contractLine;
        return getContractLineId(contractLine);
      }).filter(Boolean);

      if (regularLineIds.length > 0) {
        const freshRegularLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];

        const selectedFreshRegularLines = freshRegularLines.filter(line => {
          const lineId = getContractLineId(line);
          return lineId && regularLineIds.includes(lineId);
        });

        const regularLinesPaused = selectedFreshRegularLines.length > 0 && selectedFreshRegularLines.every(line => {
          if (!line) return false;
          if ('lineBillingStatus' in line) {
            return line.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
              line.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause;
          }
          if ('isPaused' in line) {
            return line.isPaused === true;
          }
          return false;
        });

        allLinesPaused = allLinesPaused && regularLinesPaused;
      }
    }

    if (hasVariableLines) {
      const variableLineIds = selectedVariableLines.map(line => {
        const contractLine = line.payload.contractLine;
        return getContractLineId(contractLine);
      }).filter(Boolean);

      if (variableLineIds.length > 0) {
        const freshVariableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

        const selectedFreshVBLines = freshVariableLines.filter(line => {
          const lineId = getContractLineId(line);
          return lineId && variableLineIds.includes(lineId);
        });

        const variableLinesPaused = selectedFreshVBLines.length > 0 && selectedFreshVBLines.every(line => {
          if (!line) return false;
          if ('lineBillingStatus' in line) {
            return line.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
              line.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause;
          }
          if ('isPaused' in line) {
            return line.isPaused === true;
          }
          return false;
        });

        allLinesPaused = allLinesPaused && variableLinesPaused;
      }
    }

    setIsContractLinePauseToggleEnable(allLinesPaused);

  }, [selectedLines, selectedVariableLines, refreshTrigger, contractId,
    cachedBCContractLines, cachedVariableContractLines, hasRegularLines, hasVariableLines]);

  useEffect(() => {
    const lineSubscription = lineRefreshObservable.subscribe(() => {
      setRefreshTrigger(prev => prev + 1);
    });

    const variableLineSubscription = variableLineRefreshObservable.subscribe(() => {
      setRefreshTrigger(prev => prev + 1);
    });

    return () => {
      lineSubscription.unsubscribe();
      variableLineSubscription.unsubscribe();
    };
  }, []);

  const enableLinePause = activeSelectedLines.length > 0 && !hasExcludedStatuses;

  return (
    <>
      {(visibleModal === VisibleModal.PAUSE && contract) && (
        <PauseBillingModal
          onClose={handleCloseModal}
          pauseBilling={pauseBilling}
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          isLoadingReasonCodes={isLoading}
        />
      )}

      {(visibleModal === VisibleModal.UPDATE && contract) && (
        <UpdateBillingModal
          onClose={handleCloseModal}
          updatePauseBilling={updatePauseBilling}
          billingData={contract.billing}
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          isLoadingReasonCodes={isLoading}
        />
      )}

      {(visibleModal === VisibleModal.RESUME && contract) && (
        <ResumeBillingModal
          onClose={handleCloseModal}
          resumeBilling={resumeBilling}
          billingData={contract.billing}
        />
      )}

      {(visibleModal === VisibleModal.PAUSE_CONTRACT_LINE && contract) && (
        <PauseContractLineModal
          onClose={handleCloseModal}
          pauseContractLine={pauseContractLine}
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          selectedLines={selectedFreshBCLines}
          selectedVariableLines={selectedFreshVariableLines}
          isLoadingReasonCodes={isLoading}
          readOnly={readOnly}
        />
      )}

      {(visibleModal === VisibleModal.RESUME_CONTRACT_LINE && contract) && (
        <ResumeContractLineModal
          onClose={handleCloseModal}
          resumeContractLine={resumeContractLine}
          selectedLines={selectedLines}
          selectedVariableLines={selectedVariableLines}
          isLoadingReasonCodes={isLoading}
          readOnly={readOnly}
        />
      )}

      {(visibleModal === VisibleModal.UPDATE_CONTRACT_LINE && contract) && (
        <PauseContractLineModal
          onClose={handleCloseModal}
          pauseContractLine={pauseContractLine}
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          selectedLines={selectedFreshBCLines}
          selectedVariableLines={selectedFreshVariableLines}
          isLoadingReasonCodes={isLoading}
          readOnly={readOnly}
        />
      )}

      {/* Contract line toggle - only when feature enabled AND lines selected AND no excluded statuses */}
      {/* For readOnly (Reader/User), only show when lines are already paused */}
      {(enableLinePause || disabled) && (!readOnly || isContractLinePauseToggleEnable) && (
        <ToggleOption
          id="pause-billing-contract-line"
          isEnable={isContractLinePauseToggleEnable}
          onChange={handleContractLineToggleChange}
          disabled={disabled}
          title={(
            <CustomToggleTitleComponent
              disabled={disabled}
              title="Pause Billing"
              isPaused={isContractLinePauseToggleEnable}
              onClick={() => setVisibleModal(VisibleModal.UPDATE_CONTRACT_LINE)}
            />
          )}
        />
      )}

    </>
  );
}
